import { useEffect, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import _ from 'lodash';

import {
    DocRequirementTypes,
    ProbabilityOfOffer,
    DocRequirementFileTypes,
    TOffersDataRequirements
} from './types';

type DocRequirementCodes = 'ADR' | 'BP' | 'CA' | 'MA' | 'BS' | 'VAT';

type AmplitudetradingHistoryViewedRequiredDocStauses = Omit<
    Parameters<typeof tracking.ampli.tradingHistoryViewed>[0],
    | 'Page version'
    | 'Requested product type'
    | 'loan_probability'
    | 'requested_amount'
>;

export function useAmplitudetradingHistoryViewed(
    ready: boolean,
    documentRequirements: TOffersDataRequirements,
    requestedProductType: string,
    probabilityOfOffer: ProbabilityOfOffer,
    requestedAmount: number,
    suggestedAmount: number | undefined,
    isBusinessDetailsReductionSplitTest?: boolean,
    isAutoEligible?: boolean
) {
    const [amptradingHistoryViewedFired, setAmptradingHistoryViewedFired] =
        useState(false);

    useEffect(() => {
        if (ready && !amptradingHistoryViewedFired) {
            setAmptradingHistoryViewedFired(true);
            const amplitudeDocRequirementStatus =
                amplitudeDocRequirementStatusEventProperties(
                    documentRequirements,
                    isBusinessDetailsReductionSplitTest
                );

            tracking.ampli.tradingHistoryViewed({
                'Page version': 'v2',
                'Requested product type': requestedProductType,
                requested_amount: requestedAmount,
                loan_probability: probabilityOfOffer,
                suggested_amount: suggestedAmount,
                is_auto_eligible: isAutoEligible,
                ...amplitudeDocRequirementStatus
            });
        }
    }, [
        amptradingHistoryViewedFired,
        documentRequirements,
        probabilityOfOffer,
        ready,
        requestedAmount,
        requestedProductType,
        suggestedAmount,
        isBusinessDetailsReductionSplitTest,
        isAutoEligible
    ]);
}

export function docRequirementStatusEventPropertiesOldTradingHistory(
    documentRequirements: any
): AmplitudetradingHistoryViewedRequiredDocStauses {
    function getStatusFromCode(code: DocRequirementCodes) {
        const firstDocRequirementOfType = _.find(
            documentRequirements,
            // Using "any" as the old method serializing doc requirements isn't typed on business accounts
            (requirement: any) => {
                return _.find(
                    requirement.dataRequirements,
                    (dataRequirement) => {
                        return dataRequirement.type === code;
                    }
                );
            }
        );

        const optionalStatus = _.find(
            firstDocRequirementOfType?.dataRequirements,
            (dataRequirement) => {
                return dataRequirement.type === code;
            }
        )?.optional;

        if (optionalStatus) return 'Optional';
        if (optionalStatus === false) return 'Required';

        return 'Not required';
    }

    return {
        'Aged debtor report required': getStatusFromCode('ADR'),
        'Business plan required': getStatusFromCode('BP'),
        'Company accounts required': getStatusFromCode('CA'),
        'Management accounts required': getStatusFromCode('MA'),
        'Bank statements required': getStatusFromCode('BS'),
        'VAT returns required': getStatusFromCode('VAT')
    };
}
export function amplitudeDocRequirementStatusEventProperties(
    docRequirements: TOffersDataRequirements,
    businessDetailsReductionSplitTest?: boolean
): AmplitudetradingHistoryViewedRequiredDocStauses {
    return {
        'Aged debtor report required':
            getStatusFromRequirementType('aged_debtor_report'),
        'Business plan required': getStatusFromRequirementType('business_plan'),
        'Company accounts required':
            getStatusFromRequirementType('company_accounts'),
        'Management accounts required': getStatusFromRequirementType(
            'management_accounts'
        ),
        'Bank statements required':
            getStatusFromRequirementType('bank_statement'),
        'VAT returns required': getStatusFromRequirementType('vat_return')
    };

    function getStatusFromRequirementType(type: DocRequirementTypes) {
        const docRequirementOfType = docRequirements[0]?.actions.find(
            (requirement) => {
                return requirement.type === type;
            }
        );

        if (!docRequirementOfType)
            return businessDetailsReductionSplitTest && type === 'vat_return'
                ? 'Unknown'
                : 'Not required';

        if (docRequirementOfType?.is_optional === true)
            return businessDetailsReductionSplitTest && type === 'vat_return'
                ? 'Unknown'
                : 'Optional';

        if (docRequirementOfType?.is_optional === false) return 'Required';

        return 'Not required';
    }
}

export function amplitudeDocUploadClickEvent(
    documentType: DocRequirementFileTypes
) {
    switch (documentType) {
        case 'aged debtor report':
            tracking.ampli.agedDebtorReportUploadClicked();
            break;
        case 'business plan':
            tracking.ampli.businessPlanUploadClicked();
            break;
        case 'business accounts':
            tracking.ampli.companyAccountsUploadClicked();
            break;
        case 'management accounts':
            tracking.ampli.managementAccountsUploadClicked();
            break;
        case 'bank statement':
            tracking.ampli.uploadBankStatementsClicked();
            break;
        case 'vat return':
            tracking.ampli.vatReturnsUploadClicked();
            break;
        default:
            break;
    }
}

export function amplitudeDocUploadEvent(documentType: DocRequirementFileTypes) {
    switch (documentType) {
        case 'aged debtor report':
            tracking.ampli.agedDebtorReportUploaded();
            break;
        case 'business plan':
            tracking.ampli.businessPlanUploaded();
            break;
        case 'business accounts':
            tracking.ampli.companyAccountsUploaded();
            break;
        case 'management accounts':
            tracking.ampli.managementAccountsUploaded();
            break;
        case 'bank statement':
            tracking.ampli.bankStatementsUploaded();
            break;
        case 'vat return':
            tracking.ampli.vatReturnsUploaded();
            break;
        default:
            break;
    }
}
